import React, { Fragment, useEffect, useState } from 'react';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import * as ReactIs from 'react-is';

const isComponent = (child, components = {}) => {
  if (isArray(child)) return false;
  if (!isObject(child)) return false;
  const keys = Object.keys(child);
  if (keys.length !== 1) return false;
  const searchKey = keys[0].split('.')[0];
  return Object.keys(components).includes(searchKey);
};

const isAsset = (value) =>
  typeof value === 'string' &&
  !value.startsWith('https://media.starlingbank.com') &&
  value.match(/(.jpeg|.jpg|.png|.gif|.svg|.webp|.jp2)$/);

function partitionProps(data, components, assets) {
  const children = [];

  function traverseProps(data) {
    if (isArray(data)) return data.reduce((acc, data) => traverseProps(data) && acc.concat([traverseProps(data)]), []);
    if (isComponent(data, components)) return children.push(data) && undefined;
    if (isObject(data))
      return Object.keys(data).reduce((acc, prop) => {
        const value = traverseProps(data[prop]);
        return value !== undefined ? ((acc[prop] = value), acc) : acc;
      }, {});
    if (isAsset(data)) return assets[data.substring(1)];
    return String(data);
  }

  const props = traverseProps(data);

  return [props, children.length ? children : undefined];
}

function componentObjectMapper(componentList, componentObject) {
  const componentName = Object.keys(componentObject)[0];
  const componentProps = componentObject[componentName];
  const [defaultComponent, namedComponent] = componentName.split('.');

  return [
    namedComponent ? componentList[defaultComponent][namedComponent] : componentList[defaultComponent],
    componentName,
    componentProps
  ];
}

const CLIENT_ONLY = [
  'BusinessSupport',
  'CurrentJobOpenings',
  'InteractiveQuiz',
  'OverdraftEligibility',
  'Partners',
  'PostOfficeFinderGrid',
  'TravelTool',
  'BusinessProductComparisonTool'
];

function ClientOnly({ children }) {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return null;
  }

  return children;
}

export function componentDispatcher({ components, source, meta, content, assets, pathName }) {
  if (source === undefined) return null;

  return source.map((componentObject, index) => {
    const [Component, componentName, componentProps] = componentObjectMapper(components, componentObject);
    const [props, children] = partitionProps(componentProps, components, assets);
    const Wrapper = CLIENT_ONLY.includes(componentName) ? ClientOnly : Fragment;

    if (Component.$$typeof === ReactIs.Lazy) {
      return (
        <React.Suspense key={`${componentName}-${index}`} fallback={null}>
          <Wrapper>
            <Component
              {...props}
              {...content}
              {...meta}
              pathName={pathName}
              children={componentDispatcher({ components, source: children, meta, content, assets, pathName })}
            />
          </Wrapper>
        </React.Suspense>
      );
    }

    // Don't wrap <Component /> with anything as doing so breaks uses of React.cloneElement (e.g. in the List component)
    return (
      <Component
        key={`${componentName}-${index}`}
        {...props}
        {...content}
        {...meta}
        pathName={pathName}
        children={componentDispatcher({ components, source: children, meta, content, assets, pathName })}
      />
    );
  });
}
